import { HomePlanSummary } from "./plans/HomePlanSummary";
import { Link } from "react-router-dom";
import {
  ArrowDown,
  ArrowUpRight,
  FolderHeart,
  Layers,
  Radar,
  Search,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import FeatureShowcase from "./home/FeatureShowcase";
import "./home/home.css";

const tools = [
  {
    title: "Set your next goal",
    text: "Explore sets and find the cards that complete your collection.",
    href: "/sets",
    icon: Layers,
    tone: "violet",
  },
  {
    title: "Chase smarter",
    text: "Watch your favorites and hunt for opportunities with matching alerts.",
    href: "/watchlists",
    icon: Radar,
    tone: "pink",
  },
  {
    title: "Find your card",
    text: "Search by name, set, number, and language. Get the exact match.",
    href: "/cards",
    icon: Search,
    tone: "cyan",
  },
];

export default function Hero(_props: { onAuthClick?: () => void }) {
  return (
    <main className="llama-home">
      <section className="home-hero">
        <div className="home-wrap hero-grid">
          <div className="hero-copy">
            <p className="home-eyebrow">
              <span /> FOR THE LOVE OF THE COLLECT
            </p>
            <h1>
              Your collection.
              <br />
              Your next chase.
              <br />
              <em>Your herd.</em>
            </h1>
            <p className="hero-description">
              The Pokémon collector hub that brings your binders, market
              insights, and AI tools together. From your first pull to your next
              grail.
            </p>
            <div className="home-actions">
              <Link to="/virtual-binder" className="home-button primary">
                Build your Binder <ArrowUpRight size={19} />
              </Link>
              <Link to="/market" className="home-button secondary">
                Explore the market <TrendingUp size={18} />
              </Link>
            </div>
            <a className="hero-tour" href="#platform-preview">
              <span>
                <ArrowDown size={16} />
              </span>{" "}
              See what you can do
            </a>
          </div>
          <div className="hero-art">
            <img
              src="/home/crystal-llama.webp"
              alt="A crystal blue Llama beneath a neon pink sunset, with Join the Herd lettering"
              width="1000"
              height="1000"
              fetchPriority="high"
            />
            <div className="hero-chip chip-one">
              <FolderHeart size={17} /> Big Binder energy.
            </div>
            <div className="hero-chip chip-two">
              <Sparkles size={17} /> A smarter next move.
            </div>
          </div>
        </div>
        <div className="hero-ribbon">
          <span>COLLECT.</span>
          <span>DISCOVER.</span>
          <span>CONNECT.</span>
          <span>JOIN THE HERD.</span>
        </div>
      </section>

      <section
        id="platform-preview"
        className="home-wrap showcase-section"
        aria-labelledby="preview-title"
      >
        <div className="home-section-heading">
          <div>
            <p className="home-eyebrow">YOUR COLLECTION, IN MOTION</p>
            <h2 id="preview-title">More than a stack of cards.</h2>
          </div>
          <p>
            Take a quick tour.
            <br />
            Then make it yours.
          </p>
        </div>
        <FeatureShowcase />
      </section>

      <HomePlanSummary />

      <section className="home-market" aria-labelledby="market-title">
        <div className="home-wrap market-grid">
          <div className="market-visual" aria-hidden="true">
            <div className="market-chart">
              <div className="mini-label">MARKET INSIGHTS</div>
              <h3>
                Know the story
                <br />
                behind the price.
              </h3>
              <svg viewBox="0 0 500 180">
                <defs>
                  <linearGradient
                    id="home-chart-fill"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor="#37e4e0" stopOpacity=".3" />
                    <stop offset="100%" stopColor="#37e4e0" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path
                  d="M0 140 L50 130 L100 143 L150 90 L200 108 L250 64 L300 83 L350 28 L400 45 L450 20 L500 35 L500 180 L0 180Z"
                  fill="url(#home-chart-fill)"
                />
                <path
                  d="M0 140 L50 130 L100 143 L150 90 L200 108 L250 64 L300 83 L350 28 L400 45 L450 20 L500 35"
                  fill="none"
                  stroke="#37e4e0"
                  strokeWidth="4"
                />
              </svg>
              <p>Illustrative market chart</p>
              <div className="market-tags">
                <span>Price history</span>
                <span>Source evidence</span>
                <span>Exact variants</span>
              </div>
            </div>
          </div>
          <div>
            <p className="home-eyebrow">LESS GUESSWORK. MORE CONTEXT.</p>
            <h2 id="market-title">
              Every chase starts
              <br />
              with a little insight.
            </h2>
            <p className="home-body">
              Explore market guides, available pricing evidence, and recorded
              trends. Know which card, printing, and condition you're looking at
              before your next move.
            </p>
            <Link className="home-button primary" to="/market">
              Open Market Insights <ArrowUpRight size={19} />
            </Link>
          </div>
        </div>
      </section>

      <section
        className="home-wrap home-tools"
        aria-label="More collector tools"
      >
        <div className="home-section-heading">
          <div>
            <p className="home-eyebrow">KEEP THE CHASE GOING</p>
            <h2>Your next discovery is here.</h2>
          </div>
        </div>
        <div className="home-tools-grid">
          {tools.map(({ title, text, href, icon: Icon, tone }) => (
            <Link key={href} to={href} className={`home-tool ${tone}`}>
              <Icon size={28} />
              <h3>{title}</h3>
              <p>{text}</p>
              <ArrowUpRight className="tool-arrow" size={22} />
            </Link>
          ))}
        </div>
      </section>

      <section
        className="home-wrap home-community"
        aria-labelledby="community-title"
      >
        <img
          src="/home/herd-neon.webp"
          alt="Join the Herd in glowing cyan and pink lettering"
          width="700"
          height="700"
          loading="lazy"
        />
        <div>
          <p className="home-eyebrow">FROM ONE COLLECTOR TO ANOTHER</p>
          <h2 id="community-title">
            Your cards bring
            <br />
            people together.
          </h2>
          <p>
            Discover collector shops, find your next card, or give your
            collection a storefront of its own.
          </p>
          <div className="home-actions">
            <Link to="/marketplace/discover" className="home-button primary">
              Discover collector shops <ArrowUpRight size={18} />
            </Link>
            <Link to="/seller/shop" className="home-button secondary">
              Create your shop
            </Link>
          </div>
          <Link className="community-lounge" to="/llama-lounge">
            Llama Lounge · Coming soon <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>
    </main>
  );
}
